// src/hooks/useNotificationsWS.js
//
// Best-effort live push for new system notifications (currently just
// low-stock alerts — see services.CheckLowStock on the backend), reusing
// the exact same /ws endpoint and connection lifecycle
// useSystemConfigWS.js already opens for live settings updates — just
// filtered to a different message "type". This is deliberately a SEPARATE
// socket connection rather than sharing one with useSystemConfigWS: each
// hook owns its own concern, and a missed/failed connection here never
// affects the config-update socket (or vice versa). NotificationBell also
// polls the REST endpoints on an interval, so a page where the socket
// briefly drops (or fails to connect at all) still gets the alert — just
// not instantly.
import { useEffect, useRef } from "react";

// defaultWsUrl mirrors services/api.js's own API_URL/ASSET_BASE_URL
// runtime-origin fallback (this feature's own plan doc: one static build
// has to work on every tenant's own subdomain, so nothing here can be one
// fixed, build-time-baked URL) — process.env.REACT_APP_WS_URL stays a
// LOCAL-DEV-only override (frontend/.env.example) for when CRA's dev
// server, :3000, and the Go backend, :8080, are different origins; every
// real per-subdomain production build instead derives ws(s)://<this exact
// host>/ws live, in the browser, matching whichever protocol the page
// itself loaded over (an https: page always needs wss:, never plain ws:,
// or the browser refuses the mixed-content connection outright).
function defaultWsUrl() {
  const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${window.location.host}/ws`;
}

export const useNotificationsWS = (onNotification) => {
  const wsRef = useRef(null);
  const onNotificationRef = useRef(onNotification);

  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    const wsUrl = process.env.REACT_APP_WS_URL || defaultWsUrl();

    const token = localStorage.getItem("token");
    if (!token) return;

    const separator = wsUrl.includes("?") ? "&" : "?";
    const socket = new WebSocket(`${wsUrl}${separator}token=${encodeURIComponent(token)}`);
    wsRef.current = socket;

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "notification" && data.notification && onNotificationRef.current) {
          onNotificationRef.current(data.notification);
        }
      } catch (err) {
        console.error("Invalid JSON from WS:", event.data);
      }
    };

    socket.onerror = () => {
      console.log("📡 Notifications WS: checking connection...");
    };

    return () => {
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        if (socket.readyState === WebSocket.OPEN) {
          socket.close();
        } else if (socket.readyState === WebSocket.CONNECTING) {
          socket.onopen = () => socket.close();
        }
      }
    };
  }, []);
};
